import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Check, Star, ArrowUpRight, ChevronRight, Cpu, Sparkles } from "lucide-react";

const aiTiers = [
  {
    id: "ai-sandbox",
    kicker: "// AI TIER 01 · SANDBOX",
    name: "Private AI Sandbox & Knowledge Base",
    subtitle: "Secure, offline AI search over your internal documents.",
    buildFee: "$5,000 – $8,500",
    price: 750,
    tagline: "Zero data-leakage AI for professional service firms.",
    intro:
      "For law offices, medical clinics, accounting firms, and real-estate teams needing secure, offline AI search across internal documents — with no data leakage risk to third-party APIs.",
    features: [
      { title: "Hardware Setup", desc: "Install & performance-tune on client's existing hardware (Mac Studio / workstation with 32GB+ unified memory or 16GB+ VRAM GPU)." },
      { title: "Model Engine", desc: "Deployment of quantized 7B–14B models (Llama 3 / Qwen / Mistral) optimized for local inference." },
      { title: "Document RAG System", desc: "Local vector DB (Qdrant / Chroma) connecting to internal PDFs, manuals, and SOPs." },
      { title: "User Interface", desc: "Web-based team chat interface (Open-WebUI / AnythingLLM) with role-based access." },
      { title: "Onboarding", desc: "Staff training session + customized system prompt playbook." },
    ],
    cta: "Scope AI Sandbox",
    ctaHref: "/terminal?tier=ai-sandbox",
    commitment: "One-time build fee + monthly maintenance retainer.",
    popular: false,
  },
  {
    id: "ai-onprem",
    kicker: "// AI TIER 02 · ON-PREM",
    name: "Enterprise On-Prem Node & Multi-Agent Engine",
    subtitle: "Replace cloud API fees with private, internal AI infrastructure.",
    buildFee: "$15,000 – $28,000",
    price: 1800,
    tagline: "Mid-market private AI — 50 to 200 employees.",
    intro:
      "For mid-market companies looking to replace high-volume cloud API fees with an internal, multi-department private AI infrastructure that scales across teams.",
    features: [
      { title: "Hardware Architecture", desc: "Dedicated on-premise workstation or rack-server (Dual RTX 4090 / Quad RTX 6000 or Mac Studio Cluster)." },
      { title: "Model Engine", desc: "Multi-model pipeline running 32B–70B parameter models with dynamic task routing." },
      { title: "Deep RAG Integrations", desc: "Direct indexing hooks for SharePoint, Google Drive, Notion, local NAS — with automated re-indexing." },
      { title: "Agentic Workflows", desc: "Custom AI agents per team (Red / Green / Blue teams for sales ops, technical docs, marketing)." },
      { title: "Security & Governance", desc: "Local API deployment, audit logging, PII masking, role-based data permissions." },
      { title: "Support", desc: "SLA support, model updates, and quarterly context-engineering sprints." },
    ],
    cta: "Scope Enterprise On-Prem",
    ctaHref: "/terminal?tier=ai-onprem",
    commitment: "One-time build fee + monthly SLA retainer.",
    popular: true,
  },
  {
    id: "ai-appliance",
    kicker: "// AI TIER 03 · APPLIANCE",
    name: "Turnkey On-Prem Appliance & Custom Fine-Tuning",
    subtitle: "Air-gapped, zero-cloud, proprietary fine-tuned models.",
    buildFee: "$45,000 – $85,000+",
    price: 3500,
    priceSuffix: "+",
    tagline: "For regulated enterprises: healthcare, finance, defense.",
    intro:
      "For highly regulated enterprises — healthcare networks, financial institutions, defense contractors — requiring air-gapped, zero-cloud operational environments with proprietary fine-tuned models.",
    features: [
      { title: "Custom Data Pipeline", desc: "Full data sanitization, formatting, and labeling on company historical data (30–50% of build effort)." },
      { title: "Domain Fine-Tuning", desc: "LoRA / QLoRA fine-tuning of 13B–70B open-weight models on proprietary company knowledge." },
      { title: "Air-Gapped Infrastructure", desc: "Complete offline enterprise deployment with hardware acceleration (vLLM / TensorRT-LLM backends)." },
      { title: "Enterprise Integration", desc: "Custom webhooks, MCP integrations into internal ERPs, CRMs, or software applications." },
      { title: "Governance & Compliance", desc: "HIPAA / SOC2 compliance documentation, local vulnerability scanning, dedicated SLA support." },
      { title: "MLOps", desc: "Dedicated MLOps, model-drift evaluation, and quarterly retraining cycles." },
    ],
    cta: "Book Discovery Call",
    ctaHref: "/terminal?tier=ai-appliance",
    commitment: "Build fee excludes physical hardware cost. Retainer range: $3,500–$5,000/mo.",
    popular: false,
  },
];

const auditTiers = [
  {
    id: "audit-small",
    kicker: "// AUDIT TIER 01 · READINESS",
    name: "AI Readiness & Vulnerability Assessment",
    subtitle: "For small business, professional practices, and startups.",
    audit: true,
    fee: "$3,500 – $5,000",
    timeline: "1–2 week delivery",
    tagline: '"Shadow AI" scan + safe adoption roadmap.',
    intro:
      "For local service businesses, professional practices (law, accounting, real estate), and startups looking to adopt AI safely — or evaluate current unsanctioned tool usage.",
    features: [
      { title: "Shadow AI & Risk Scan", desc: "Audit of current employee software usage to identify unauthorized cloud AI use and potential data leaks." },
      { title: "Workflow Efficiency Mapping", desc: "Review of core administrative, sales, and content workflows to identify top 3 high-ROI automation opportunities." },
      { title: "Data Privacy Check", desc: "Baseline review of customer data handling to ensure compliance with data privacy standards before deploying AI." },
      { title: "Deliverables", desc: "Executive Summary Report, Risk Scorecard, and prioritized 'AI Implementation Roadmap' recommending open-source or commercial deployments." },
    ],
    cta: "Scope Readiness Audit",
    ctaHref: "/terminal?tier=audit-small",
    commitment: "One-time engagement. Converts to Tier 1 Local AI Sandbox.",
    popular: false,
  },
  {
    id: "audit-mid",
    kicker: "// AUDIT TIER 02 · ARCHITECTURE",
    name: "Comprehensive AI Architecture & Security Audit",
    subtitle: "For mid-market companies (25–250 employees).",
    audit: true,
    fee: "$8,500 – $15,000",
    timeline: "2–3 week delivery",
    tagline: "Deep-dive on security posture, data readiness, integration potential.",
    intro:
      "For growing companies with existing digital infrastructure who need a rigorous evaluation of their security posture, data readiness, and system integration potential before deploying private AI.",
    features: [
      { title: "Data Pipeline & Infrastructure Assessment", desc: "Comprehensive audit of internal databases, file shares (SharePoint, Google Workspace, local NAS), and APIs for vector-DB (RAG) readiness." },
      { title: "Governance & Compliance Review", desc: "Deep evaluation of current data policies against HIPAA, SOC2, and GDPR — ensuring zero data leakage to public AI models." },
      { title: "Tech Stack & Model Benchmarking", desc: "Technical evaluation of existing cloud/SaaS software vs. potential cost savings of on-premise local LLM deployments." },
      { title: "Security & Red Teaming", desc: "Baseline prompt-injection testing and role-based access control (RBAC) validation on current AI tools." },
      { title: "Deliverables", desc: "25+ page Technical Audit Document, Architecture Diagram, ROI & Cost-Savings Matrix, and a 6-month AI Deployment Blueprint." },
    ],
    cta: "Scope Architecture Audit",
    ctaHref: "/terminal?tier=audit-mid",
    commitment: "One-time engagement. Converts to Tier 2 Enterprise On-Prem Node.",
    popular: true,
  },
  {
    id: "audit-enterprise",
    kicker: "// AUDIT TIER 03 · RED TEAM",
    name: "Enterprise AI Governance, Security & Red Teaming Audit",
    subtitle: "For regulated enterprises with custom AI deployments.",
    audit: true,
    fee: "$25,000 – $45,000+",
    timeline: "4–6 week delivery",
    tagline: "Adversarial red-team, algorithm audit, and governance framework.",
    intro:
      "For large enterprises, healthcare networks, financial institutions, and government contractors with complex regulatory requirements and custom AI deployments already in production.",
    features: [
      { title: "Adversarial Red Teaming", desc: "Rigorous penetration testing of proprietary LLMs and agents — jailbreaks, prompt injection, data extraction, model poisoning." },
      { title: "Algorithm & Bias Auditing", desc: "Review of model training data, output alignment, and system instructions for regulatory compliance and legal defensibility." },
      { title: "Enterprise Architecture & MLOps Audit", desc: "In-depth review of vector store efficiency, model latency, API security, and server infrastructure scaling limits." },
      { title: "Policy & Governance Framework", desc: "Complete enterprise AI usage policies, employee guidelines, and automated audit-logging specifications." },
      { title: "Deliverables", desc: "Enterprise Governance & Risk Matrix, Security Remediation Plan, C-Suite Presentation, and full Technical Specs for Tier 3 Appliance deployments." },
    ],
    cta: "Book Discovery Call",
    ctaHref: "/terminal?tier=audit-enterprise",
    commitment: "One-time engagement. Converts to Tier 3 Fine-Tuned Appliance.",
    popular: false,
  },
];

const tiers = [
  {
    id: "footprint",
    kicker: "// TIER 01 · FOOTPRINT",
    name: "Digital Footprint",
    subtitle: "The Baseline Presence Engine",
    price: 375,
    tagline: "Essential presence.",
    intro:
      "Perfect for local businesses, startups, and service providers who need to establish absolute digital credibility, lock down local search basics, and stay active online without a massive budget.",
    features: [
      { title: "1 Monthly Authority Article", desc: "Fully optimized, 800-word industry / local keyword blog post to build core search relevance." },
      { title: "2 Social Media Posts / Week", desc: "Dynamic, branded content deployed across up to 2 platforms (Facebook & Instagram or LinkedIn)." },
      { title: "Google Business Profile Optimization", desc: "Initial setup audit + 1 monthly targeted local authority post to signal active map operations." },
      { title: "On-Page 'Quick Wins' Audit", desc: "One-time technical meta-tag and structural keyword optimization for your top 3 web pages." },
      { title: "Automated Monthly Dashboard", desc: "Streamlined data tracking showing organic growth and impressions." },
      { title: "Support", desc: "Asynchronous email & portal support." },
    ],
    cta: "Get Started with Footprint",
    ctaHref: "/terminal?tier=footprint",
    popular: false,
    commitment: "Billed monthly. Pause or cancel anytime.",
  },
  {
    id: "growth",    kicker: "// TIER 02 · GROWTH",
    name: "Growth & Authority",
    subtitle: "The Inbound Pipeline Generator",
    price: 750,
    tagline: "Active lead generation.",
    intro:
      "Designed for growing companies ready to actively steal market share from local competitors, dominate search maps, and convert passive scrollers into paying leads.",
    features: [
      { title: "2 Monthly Authority Articles", desc: "Deep-dive, 1,000-word targeted keyword assets built to rank organically for high-intent search terms." },
      { title: "4 Social Media Posts / Week", desc: "Expanded content matrix deployed across up to 3 platforms to maximize brand touchpoints." },
      { title: "2 Short-Form Video Frameworks", desc: "Custom monthly scripts / concepts for Reels, TikTok, or Shorts. You film in 5 minutes — we handle the strategic formatting." },
      { title: "Weekly Google Business Profile Updates", desc: "4 strategic map-pack updates per month to aggressively push your local ranking higher." },
      { title: "1 Monthly Email Newsletter", desc: "Custom designed and written outreach to engage your existing customer list and drive repeat business." },
      { title: "Review Generation Framework", desc: "Automated templates to systematically acquire five-star Google reviews from your clients." },
      { title: "Support", desc: "Quarterly 30-min live strategy calibration call + priority email." },
    ],
    cta: "Scale Your Growth Now",
    ctaHref: "/terminal?tier=growth",
    popular: true,
    commitment: "Billed monthly. Pause or cancel anytime.",
  },
  {
    id: "market-leader",
    kicker: "// TIER 03 · LEADER",
    name: "Market Leader",
    subtitle: "The Omnichannel Outsourced Marketing Dept.",
    price: 1500,
    tagline: "Complete outsourcing.",
    intro:
      "Built for established brands, multi-location companies, or aggressive businesses looking for total market dominance, omnichannel short-form video deployment, and end-to-end management.",
    features: [
      { title: "4 Monthly High-Impact Articles", desc: "Complete search domination content engine featuring 1,200+ word, highly comprehensive keyword pieces." },
      { title: "5 Premium Social Posts / Week", desc: "Full multi-platform coverage (up to 4 platforms) keeping your brand universally present." },
      { title: "4 Fully Edited Short-Form Videos", desc: "Complete production cycle. We script it, you shoot it, and our team handles professional editing, captions, b-roll, and deployment." },
      { title: "Omnichannel Local SEO", desc: "Weekly Google Business updates coupled with automated local citation syncing across 40+ industry directories." },
      { title: "2 Dedicated Email Campaigns / Month", desc: "1 value-driven community newsletter plus 1 hard-hitting promotional campaign to maximize list revenue." },
      { title: "Paid Ad Funnel Architecture", desc: "Turnkey management, optimization, and asset creation for Google Local Services Ads or Meta Retargeting funnels (ad spend separate)." },
      { title: "Support", desc: "Monthly 30-min live strategy call + custom lead-attribution dashboard + VIP Slack support." },
    ],
    cta: "Partner with TraceElements",
    ctaHref: "/terminal?tier=market-leader",
    popular: false,
    commitment: "Billed monthly. 3-month initial commitment preferred.",
  },
];

const faqs = [
  {
    q: "How much of my time will this take?",
    a: "Very little. We run on a highly streamlined automation model. For Tier 1 and 2, you spend less than 10 minutes a month clicking 'Approve' on your content calendar inside your client portal. For Tier 3, you just record the short videos using our exact scripts, and we handle the rest.",
  },
  {
    q: "Are there long-term contracts?",
    a: "No. Tiers 1 and 2 operate on a strict month-to-month basis. You can pause or cancel your subscription at any time. We let our results dictate client retention.",
  },
  {
    q: "Can I switch tiers later?",
    a: "Absolutely. You can easily upgrade or downgrade your growth framework directly inside your TraceElements portal as your business demands change.",
  },
];

export default function Pricing() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location]);

  return (
    <div className="bg-[#0B0C10]" data-testid="pricing-page">
      {/* Header */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden" data-testid="pricing-hero">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// SERVICES</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            Monthly retainers.
            <br />
            <span className="text-[#FF3355]">Private AI deployments.</span>
          </h1>
          <p className="mt-8 text-[#C5C6C7] max-w-3xl leading-relaxed text-lg">
            Two engines, one accountable firm. Growth-marketing retainers for scaling brands, and
            private on-prem LLM deployment for regulated enterprises that can&apos;t send data to third-party APIs.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.22em]">
            <a href="#growth-marketing" className="border border-[#1F2833] px-4 py-2 hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7]">// Growth Marketing Retainers ↓</a>
            <a href="#private-ai" className="border border-[#1F2833] px-4 py-2 hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7]">// Private AI Deployment ↓</a>
            <a href="#ai-audits" className="border border-[#1F2833] px-4 py-2 hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7]">// AI Risk & Compliance Audits ↓</a>
          </div>
        </div>
      </section>

      {/* Growth Marketing Retainers */}
      <section id="growth-marketing" className="py-16 lg:py-20 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// GROWTH.MARKETING · MONTHLY</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                Growth marketing retainers.
              </h2>
              <p className="mt-3 text-[#C5C6C7] max-w-2xl">Content, SEO, social, and email — deployed weekly. No long contracts.</p>
            </div>
            <div className="inline-flex items-center gap-2 border border-[#1F2833] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              <Sparkles size={12} className="text-[#FF3355]" /> Month-to-month
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
            {tiers.map((t) => <TierCard key={t.id} t={t} />)}
          </div>
        </div>
      </section>

      {/* Private AI Deployment */}
      <section id="private-ai" className="py-16 lg:py-20 border-t border-[#1F2833] scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// LOCAL.LLM · PRIVATE AI DEPLOYMENT</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                On-prem LLM deployment.
              </h2>
              <p className="mt-3 text-[#C5C6C7] max-w-3xl">
                Recalibrated to industry-standard rates for mid-market AI agencies and IT consultancies. Build fees reflect the
                technical complexity, data security requirements, and custom engineering involved in on-premise local LLM deployments.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 border border-[#1F2833] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              <Cpu size={12} className="text-[#FF3355]" /> Build fee + monthly
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
            {aiTiers.map((t) => <TierCard key={t.id} t={t} showBuildFee />)}
          </div>

          {/* Market benchmarks table */}
          <div className="mt-14 border border-[#1F2833]">
            <div className="px-5 py-3 border-b border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
              // MARKET.BENCHMARKS
            </div>
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              <div className="col-span-3">tier</div>
              <div className="col-span-3">build fee</div>
              <div className="col-span-2">monthly</div>
              <div className="col-span-4">industry comp</div>
            </div>
            {[
              { tier: "Tier 1 · Private AI Sandbox", build: "$5,000 – $8,500", monthly: "$750 / mo", comp: "Matches IT internal-tool deployment / RAG sprint rates." },
              { tier: "Tier 2 · Enterprise On-Prem Node", build: "$15,000 – $28,000", monthly: "$1,800 / mo", comp: "Standard for multi-user workflow automation + custom integrations." },
              { tier: "Tier 3 · Fine-Tuned Appliance", build: "$45,000 – $85,000+", monthly: "$3,500+ / mo", comp: "Entry rate for fine-tuning, heavy data engineering, MLOps retainers." },
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-[#1F2833] last:border-b-0 font-mono text-xs">
                <div className="md:col-span-3 text-[#F5F5F7]">{row.tier}</div>
                <div className="md:col-span-3 text-[#FF3355]">{row.build}</div>
                <div className="md:col-span-2 text-[#FF3355]">{row.monthly}</div>
                <div className="md:col-span-4 text-[#C5C6C7]">{row.comp}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Risk & Compliance Audits */}
      <section id="ai-audits" className="py-16 lg:py-20 border-t border-[#1F2833] scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// AUDIT · RISK · COMPLIANCE</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                AI risk, compliance & readiness audits.
              </h2>
              <p className="mt-3 text-[#C5C6C7] max-w-3xl">
                Before you buy or build private AI — get the risk picture. One-time, fixed-fee engagements that produce
                a real remediation plan and a defensible deployment roadmap.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 border border-[#1F2833] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              <Cpu size={12} className="text-[#FF3355]" /> One-time engagement
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
            {auditTiers.map((t) => <TierCard key={t.id} t={t} />)}
          </div>

          {/* Audit rates + pipeline conversion table */}
          <div className="mt-14 border border-[#1F2833]">
            <div className="px-5 py-3 border-b border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
              // AUDIT.RATES · PIPELINE CONVERSION
            </div>
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              <div className="col-span-3">audit tier</div>
              <div className="col-span-3">fee range</div>
              <div className="col-span-2">timeline</div>
              <div className="col-span-4">conversion goal</div>
            </div>
            {[
              { tier: "Small Business Readiness", fee: "$3,500 – $5,000", timeline: "1–2 Weeks", conv: "→ Tier 1 Local AI Sandbox ($5,000–$8,500)" },
              { tier: "Mid-Market Architecture", fee: "$8,500 – $15,000", timeline: "2–3 Weeks", conv: "→ Tier 2 Enterprise On-Prem Node ($15,000–$28,000)" },
              { tier: "Enterprise Security & Red Team", fee: "$25,000 – $45,000+", timeline: "4–6 Weeks", conv: "→ Tier 3 Fine-Tuned Appliance ($45,000–$85,000+)" },
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-[#1F2833] last:border-b-0 font-mono text-xs">
                <div className="md:col-span-3 text-[#F5F5F7]">{row.tier}</div>
                <div className="md:col-span-3 text-[#FF3355]">{row.fee}</div>
                <div className="md:col-span-2 text-[#FF3355]">{row.timeline}</div>
                <div className="md:col-span-4 text-[#C5C6C7]">{row.conv}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section id="pricing-faq" className="py-20 border-t border-[#1F2833] scroll-mt-24" data-testid="pricing-faq">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// FREQUENTLY.ASKED</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tighter mb-10">
            Common questions before you subscribe.
          </h2>
          <div className="space-y-px bg-[#1F2833] border border-[#1F2833]">
            {faqs.map((f, i) => (
              <details key={i} className="bg-[#0B0C10] p-6 group" data-testid={`pricing-faq-${i}`}>
                <summary className="cursor-pointer font-display text-lg font-semibold flex items-center justify-between gap-4 list-none">
                  <span>{f.q}</span>
                  <ChevronRight size={16} className="text-[#FF3355] group-open:rotate-90 transition-transform shrink-0" />
                </summary>
                <p className="mt-4 text-[#C5C6C7] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 border-t border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NOT SURE WHICH TIER?</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Talk to a founder. 30 minutes. No pitch deck.
            </h3>
          </div>
          <Link
            to="/terminal"
            data-testid="pricing-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors w-fit"
          >
            Book Strategy Call <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}


function TierCard({ t, showBuildFee }) {
  return (
    <div
      id={t.id}
      data-testid={`tier-${t.id}`}
      className={`relative bg-[#0B0C10] p-8 lg:p-10 flex flex-col scroll-mt-24 ${
        t.popular ? "lg:-my-6 lg:z-10 bg-[#0d1117] border border-emerald-400 shadow-[0_0_40px_rgba(52,211,153,0.2)]" : ""
      }`}
    >
      {t.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-emerald-400 text-[#0B0C10] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold te-emerald-glow-sm">
          <Star size={12} fill="currentColor" /> Most Popular
        </div>
      )}

      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B91C2C]">{t.kicker}</div>
      <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tighter leading-tight">{t.name}</h3>
      <p className="mt-2 font-mono text-xs text-[#C5C6C7]">{t.subtitle}</p>

      {t.audit ? (
        <>
          <div className="mt-8 border-l-2 border-[#FF3355] pl-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">audit fee</div>
            <div className="mt-1 font-display text-4xl lg:text-5xl font-bold text-[#FF3355]">{t.fee}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mt-1">{t.timeline}</div>
          </div>
        </>
      ) : (
        <>
          {showBuildFee && (
            <div className="mt-6 border-l-2 border-[#B91C2C] pl-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">Implementation build fee</div>
              <div className="mt-1 font-display text-2xl font-bold text-[#F5F5F7]">{t.buildFee}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mt-0.5">one-time setup</div>
            </div>
          )}

          <div className={`${showBuildFee ? "mt-5" : "mt-8"} flex items-baseline gap-2`}>
            <span className="font-display text-5xl lg:text-6xl font-bold text-[#FF3355]">
              ${t.price.toLocaleString()}{t.priceSuffix || ""}
            </span>
            <span className="font-mono text-sm text-[#C5C6C7]">/mo</span>
          </div>
        </>
      )}
      <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">{t.commitment}</div>

      <p className="mt-6 text-[#FF3355] font-display text-lg">{t.tagline}</p>
      <p className="mt-4 text-sm text-[#C5C6C7] leading-relaxed">{t.intro}</p>

      <ul className="mt-8 space-y-4 flex-1">
        {t.features.map((f, i) => (
          <li key={i} className="te-lime-accent flex items-start gap-3">
            <Check size={16} className="text-emerald-400 mt-1 shrink-0" />
            <div>
              <div className="font-mono text-sm text-[#F5F5F7]">{f.title}</div>
              <div className="text-xs text-[#C5C6C7] mt-0.5 leading-relaxed">{f.desc}</div>
            </div>
          </li>
        ))}
      </ul>

      <Link
        to={t.ctaHref}
        data-testid={`tier-cta-${t.id}`}
        className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold transition-colors ${
          t.popular
            ? "bg-[#FF3355] text-[#0B0C10] hover:bg-[#F5F5F7]"
            : "border border-[#B91C2C] text-[#F5F5F7] hover:bg-[#B91C2C]/20 hover:border-[#FF3355]"
        }`}
      >
        [ {t.cta} ] <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}
